import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, Sun, Star } from 'lucide-react';
import { ASSETS, CONTACT, SOCIAL_LINKS, GOOGLE_BUSINESS_URL } from '../data/constants';
import { AREAS } from '../data/areas';

const Footer = () => {
  return (
    <footer className="relative overflow-hidden" style={{
      background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 15%, #B0E0E6 30%, #98FB98 50%, #F0FFF0 70%, #FFFACD 85%, #FFE4B5 100%)'
    }}>
      {/* Slogan Banner */}
      <div className="border-b border-teal-300/30 py-6 relative z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-xl md:text-2xl font-['Barlow_Condensed'] uppercase tracking-wider text-teal-700 mb-1">
            Your Wellness Journey Starts Here
          </p>
          <p className="text-orange-500 font-semibold flex items-center justify-center gap-2">
            <Sun size={14} className="text-yellow-500" /> Hot Tubs • Saunas • Cold Plunges • Swim Spas <Sun size={14} className="text-yellow-500" />
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div>
            <img src={ASSETS.tropicalLogo} alt="Leisure Wellness Naples" className="h-28 mb-6" loading="lazy" />
            <p className="text-slate-700 text-sm leading-relaxed mb-6">
              Start living your{' '}
              <span className="text-orange-500 font-bold">best life</span>
              {' '}today! Premium hot tubs, swim spas, saunas and cold plunges in Naples, FL.
            </p>
            <div className="flex gap-3">
              {[
                { icon: Instagram, href: SOCIAL_LINKS.instagram, name: 'Instagram' },
                { icon: Facebook, href: SOCIAL_LINKS.facebook, name: 'Facebook' },
                { icon: Youtube, href: SOCIAL_LINKS.youtube, name: 'YouTube' }
              ].map(({ icon: Icon, href, name }) => (
                <motion.a 
                  key={href} 
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center rounded-lg hover:from-orange-500 hover:to-orange-600 transition-all shadow-md" 
                  whileHover={{ scale: 1.1 }}
                  aria-label={`Follow us on ${name}`}
                >
                  <Icon size={18} aria-hidden="true" />
                </motion.a>
              ))}
            </div>
          </div>
          
          {/* Shop Links */}
          <div>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-6 text-teal-700">Shop</h3>
            <div className="space-y-2">
              {[
                { name: 'Hot Tubs', href: '/hot-tubs' },
                { name: 'PDC Spas', href: '/pdc-spas' },
                { name: 'ClearWater Spas', href: '/clearwater-spas' },
                { name: 'Natural Rock Spas', href: '/natural-rock-spas' },
                { name: 'Saunas', href: '/saunas' },
                { name: 'Swim Spas', href: '/swim-spas' },
                { name: 'Cold Plunges', href: '/cold-plunges' }
              ].map(link => (
                <Link 
                  key={link.name} 
                  to={link.href}
                  className="block text-slate-600 hover:text-orange-500 text-sm transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mt-6 mb-4 text-teal-700">Discover</h3>
            <div className="space-y-2">
              {[
                { name: 'Wellness', href: '/wellness' },
                { name: 'About', href: '/about' },
                { name: 'Events', href: '/events' },
                { name: 'Financing', href: '/financing' },
                { name: 'Spa Butler', href: '/spa-butler' },
                { name: 'FAQ', href: '/faq' }
              ].map(link => (
                <Link 
                  key={link.name} 
                  to={link.href} 
                  className="block text-slate-600 hover:text-orange-500 text-sm transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Contact Us */}
          <div>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-6 text-teal-700">Contact Us</h3>
            <div className="space-y-3">
              <a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 text-slate-700 hover:text-orange-500 text-sm">
                <Phone size={16} className="text-teal-500" /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-slate-700 hover:text-orange-500 text-sm">
                <Mail size={16} className="text-teal-500" /> {CONTACT.email}
              </a>
              <div className="flex items-start gap-3 text-slate-700 text-sm">
                <MapPin size={16} className="text-teal-500 flex-shrink-0 mt-1" /> {CONTACT.address}
              </div>
            </div>
            
            {/* Hours Section */}
            <div id="footer-hours" className="mt-6 pt-4 border-t border-teal-200/50">
              <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase mb-3 text-teal-700">
                Store Hours
              </h4>
              <div className="space-y-1 text-sm">
                {CONTACT.detailedHours && CONTACT.detailedHours.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-slate-600">
                    <span className="font-medium">{item.day}</span>
                    <span>{item.hours}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500 italic">
                {CONTACT.hours}
              </p>
            </div>
          </div>
          
          {/* Service Areas */}
          <div>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-6 text-teal-700">Service Areas</h3>
            <div className="grid grid-cols-2 gap-1" data-testid="footer-service-areas">
              {CONTACT.serviceAreas.map(area => {
                const a = AREAS.find((x) => area.startsWith(x.city));
                const to = area.startsWith('Naples') ? '/' : a ? `/areas/${a.slug}` : '/areas';
                return (
                  <Link key={area} to={to} className="text-slate-600 hover:text-[#F88200] text-xs flex items-center gap-1 transition-colors">
                    <Sun size={10} className="text-yellow-500" /> {area}
                  </Link>
                );
              })}
            </div>
            <Link to="/hot-tub-buying-guide" className="block text-sm text-[#0E3D95] hover:text-[#F88200] font-semibold mt-3" data-testid="footer-buying-guide">Hot Tub Buying Guide</Link>
            <Link to="/contact" className="btn-primary inline-block mt-6 text-sm px-6 py-3">Get a Quote</Link>

            {/* Google Reviews Button */}
            <a
              href={GOOGLE_BUSINESS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 bg-white border-2 border-[#4285F4] text-[#0E3D95] hover:bg-[#4285F4] hover:text-white font-bold text-sm px-5 py-2.5 rounded-lg transition-colors shadow-sm"
              data-testid="footer-google-reviews"
            >
              <Star className="text-yellow-500 fill-yellow-500" size={16} /> See Our Google Reviews
            </a>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-teal-300/30 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-600 text-sm">© {new Date().getFullYear()} Leisure Wellness Naples. All Rights Reserved.</p>
          <p className="text-slate-600 text-sm flex items-center gap-2">
            <Sun size={14} className="text-yellow-500" /> Serving Naples & Southwest Florida <Sun size={14} className="text-yellow-500" />
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
